import {gsap} from "gsap";
import {Helper} from "../../../core/utils/Helper";
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {FontWeight} from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";
import {IDimOrientation, LayoutDimData, ResizeHelper} from "../../../core/utils/ResizeHelper";
import {ContinueButton} from "@nolimitcity/slot-launcher/bin/gui/buttons/concretebuttons/ContinueButton";
import {SplashViewFsOutro} from "./views/SplashViewFsOutro";
import {SplashViewFreeSpins} from "./views/SplashViewFreeSpins";
import {SplashViewSFSPickAndClick} from "./views/SplashViewSFSPickAndClick";
import {SplashViewWinCap} from "./views/SplashViewWinCap";

export interface SplashView extends PIXI.Container {
    play(): gsap.core.Timeline;

    stop(): gsap.core.Timeline;

    setData?(data: any): void;

    onResize(): void;
}

export enum SplashIndex { INTRO = 0, OUTRO = 1, PICKANDCLICK = 2, BREAKTHEBANK = 3 }

export class SplashController extends PIXI.Container implements StageLayer {

    private static _instance: SplashController;
    private _continueButton: ContinueButton;
    private _gfxBg: PIXI.Graphics;
    private _playedIndex: number | undefined = 0;
    private _onClicked: Function | undefined;

    private layoutDim: IDimOrientation = {
        landscape: {
            scale: new PIXI.Point(1, 1),
            position: new PIXI.Point(360, 360),
        },
        portrait: {
            scale: new PIXI.Point(1, 1),
            position: new PIXI.Point(360, 360)
        }
    };

    private readonly DEFAULT_TXT_STYLE = {
        fontWeight: FontWeight.BOLD,
        fontFamily: "Helvetica",
        align: "center",
        fill: "#ffffff",
        fontSize: 34,
        wordWrap: true,
        wordWrapWidth: 500,

        dropShadow: true,
        dropShadowAngle: 8,
        dropShadowBlur: 4,
        dropShadowDistance: 4,
    };

    private _views: SplashView[] = [];

    private _loop: gsap.core.Timeline;
    private _timeline: gsap.core.Timeline;

    public static get instance(): SplashController {
        return this._instance;
    }

    public get button(): ContinueButton {
        return this._continueButton;
    }

    constructor() {
        super();
        SplashController._instance = this;
        this.init();
        this.onResize();
    }

    private init(): void {

        this.createGraphic();
        this.createInteractiveModules();
        this.createPages();

        this.sortChildren();

        this.visible = false;
    }

    private createPages(): void {
        this._views.push(this.addChild(new SplashViewFreeSpins()));
        this._views.push(this.addChild(new SplashViewFsOutro(this.DEFAULT_TXT_STYLE)));
        this._views.push(this.addChild(new SplashViewSFSPickAndClick(this.DEFAULT_TXT_STYLE)));
        this._views.push(this.addChild(new SplashViewWinCap(this.DEFAULT_TXT_STYLE)));
    }

    private createInteractiveModules(): void {
        this._continueButton = this.addChild(new ContinueButton());

        this._continueButton.enable(false);
        this._continueButton.x -= this._continueButton.width / 2;
        this._continueButton.addClickCallback(() => this.clickCallback())

        this._continueButton.zIndex = 100;
        this._continueButton.alpha = 0;
    }

    protected createGraphic(): void {
        const maxAreaSize: number = 3000;
        this._gfxBg = this.addChild(new PIXI.Graphics());
        this._gfxBg.beginFill(0x000000, 0.65);
        this._gfxBg.drawRect(-maxAreaSize * 0.5, -maxAreaSize * 0.5, maxAreaSize, maxAreaSize);
        this._gfxBg.endFill();
    }

    private clickCallback(): void {
        this._timeline = Helper.setTimeline(this._timeline, new gsap.core.Timeline({
            onComplete: () => {
                Helper.setTimeline(this._loop);
                this.alpha = 1;
                this.visible = false;
            }
        }));

        if (this._onClicked) this._onClicked();

        if (this._playedIndex != undefined) this._timeline.add(this._views[this._playedIndex].stop(), 0);
        const stopDurr: number = this._timeline.duration();

        this._timeline.fromTo(this, {duration: stopDurr ? stopDurr : 0.25, alpha: 1}, {alpha: 0}, 0);
        this._continueButton.enable(false);
    }

    private tlBg: gsap.core.Timeline;

    public setBGAlpha(alpha: number, durr: number): gsap.core.Timeline {
        this.tlBg = Helper.setTimeline(this.tlBg, new gsap.core.Timeline({
            onStart: () => {
                this.visible = true;
                this.button.alpha = 0;
            },
            onComplete: () => {
                this.visible = !!this._gfxBg.alpha
            }
        }));
        this.tlBg.fromTo(this._gfxBg, {alpha: 0}, {alpha: alpha, duration: durr});
        return this.tlBg;
    }

    public setData(viewIndx: number, data: any): void {
        this._views[viewIndx] && this._views[viewIndx].setData != undefined ? this._views[viewIndx]!.setData!(data) : undefined;
    }

    public play(viewIndx?: SplashIndex, button?: boolean, setBGAlpha?: boolean, onClicked?: Function): gsap.core.Timeline {
        this._playedIndex = viewIndx;

        this._onClicked = onClicked;

        this._timeline = Helper.setTimeline(this._timeline, new gsap.core.Timeline({
            onStart: () => {
                this.visible = true;
                this.alpha = 1
            }
        }));
        if (setBGAlpha) {
            this._timeline.add(this.setBGAlpha(1, 0.25), 0);
        } else {
            this._gfxBg.alpha = 0;
        }

        if (this._playedIndex != undefined) this._timeline.add(this._views[this._playedIndex].play());

        if (button) {
            this._timeline.add([
                () => this._continueButton.enable(true),
                gsap.fromTo(this._continueButton, {duration: 0.25, alpha: 0}, {alpha: 1})
            ], "-=0.25");

            this._loop = Helper.setTimeline(this._loop, new gsap.core.Timeline({repeat: -1}));
            this._loop.to(this, {duration: 100});

            return this._loop;
        }else {
            this._continueButton.alpha = 0;
            this._continueButton.enable(false);
        }

        return this._timeline;
    }

    public enableButton(onClicked?: Function) {
        if(onClicked) this._onClicked =  onClicked;

        this._timeline = Helper.setTimeline(this._timeline, new gsap.core.Timeline({
            onStart: () => {
                this.visible = true;
                this.alpha = 1
            }
        }));
        this._timeline.add([
            () => this._continueButton.enable(true),
            gsap.fromTo(this._continueButton, {duration: 0.25, alpha: 0}, {alpha: 1})
        ], "-=0.25");

        this._loop = Helper.setTimeline(this._loop, new gsap.core.Timeline({repeat: -1}));
        this._loop.to(this, {duration: 100});

        return this._loop;
    }

    public stop(): void {
        this.clickCallback();
    }

    public onResize(): void {
        const rezDim: LayoutDimData = ResizeHelper.calcDim(this.layoutDim)

        this.position = rezDim.position;
        this.scale = rezDim.scale;

        this._continueButton.y = ResizeHelper.isLandscape ? 200 : 200;
        if (this._playedIndex != undefined)
        this._views[this._playedIndex].onResize();
    }
}
